import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtError } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import CurrencyPicker from "@/components/CurrencyPicker";
import FileUploader from "@/components/FileUploader";
import EditTemplateModal from "@/components/EditTemplateModal";
import { getCurrencyMeta } from "@/lib/currencies";
import { useConfirm } from "@/components/ConfirmDialog";
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export const PRICING_MODELS = [
    { value: "CPM", label: "CPM" },
    { value: "CPC", label: "CPC" },
    { value: "CPS", label: "CPS (cost per session)" },
    { value: "CPV", label: "CPV (cost per view)" },
    { value: "CPCV", label: "CPCV (cost per completed view)" },
    { value: "CPL", label: "CPL (cost per lead)" },
    { value: "non_skip_bumper", label: "Non-skip bumper" },
    { value: "skippable", label: "Skippable" },
];

export const CREATIVE_TYPES = [
    { value: "Display", label: "Display" },
    { value: "Video", label: "Video" },
    { value: "Audio", label: "Audio" },
    { value: "Native", label: "Native" },
    { value: "Rich Media", label: "Rich Media" },
    { value: "Trackers", label: "Trackers" },
];

export const PLATFORM_TYPES = [
    { value: "dsp", label: "DSP" },
    { value: "reporting_only", label: "Reporting Only" },
];

function daysBetween(startISO, endISO) {
    if (!startISO || !endISO) return 0;
    const s = new Date(startISO);
    const e = new Date(endISO);
    if (isNaN(s) || isNaN(e) || e < s) return 0;
    return Math.round((e - s) / 86400000) + 1;
}

export default function CampaignCreate() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const confirm = useConfirm();
    const [clients, setClients] = useState([]);
    const [form, setForm] = useState({
        client_org_id: "",
        name: "",
        start_date: new Date().toISOString().slice(0, 10),
        end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
        volume: "",
        payout: "",
        revenue: "",
        currency: "USD",
        targeting: "",
        creative_type: "Display",
        pricing_model: "CPM",
        platform_type: "dsp",
        notes: "",
    });
    const [referenceFile, setReferenceFile] = useState(null); // {id, filename, size} | null
    const [busy, setBusy] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [editingTemplate, setEditingTemplate] = useState(null);

    const loadTemplates = async () => {
        try {
            const { data } = await api.get("/campaign-templates");
            setTemplates(data || []);
        } catch (e) { setTemplates([]); }
    };

    useEffect(() => {
        (async () => {
            try {
                const [clientsRes, tplRes] = await Promise.all([
                    api.get("/clients"),
                    api.get("/campaign-templates").catch(() => ({ data: [] })),
                ]);
                setClients(clientsRes.data);
                if (clientsRes.data[0]) setForm((f) => ({ ...f, client_org_id: clientsRes.data[0].id, currency: clientsRes.data[0].currency || "USD" }));
                const tpls = tplRes.data || [];
                setTemplates(tpls);
                // If ?template=<id> in URL, auto-apply
                const fromUrl = searchParams.get("template");
                if (fromUrl && tpls.some((t) => t.id === fromUrl)) {
                    setTimeout(() => applyTemplateById(fromUrl, tpls), 50);
                }
            } catch (e) {}
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyTemplateById = (tplId, source) => {
        const list = source || templates;
        const tpl = list.find((t) => t.id === tplId);
        if (!tpl) return;
        setSelectedTemplate(tplId);
        const s = tpl.snapshot || {};
        setForm((f) => ({
            ...f,
            client_org_id: s.client_org_id || f.client_org_id,
            volume: s.volume ?? "",
            payout: s.payout ?? "",
            revenue: s.revenue ?? "",
            currency: s.currency || f.currency,
            targeting: s.targeting || "",
            creative_type: s.creative_type || f.creative_type,
            pricing_model: s.pricing_model || f.pricing_model,
            notes: s.notes || "",
        }));
        toast.success(`Pre-filled from template "${tpl.name}"`);
    };

    const applyTemplate = (tplId) => {
        applyTemplateById(tplId);
    };

    const deleteTemplate = async (tplId) => {
        const tpl = templates.find((t) => t.id === tplId);
        if (!tpl) return;
        const ok = await confirm({
            title: "Delete this template?",
            description: `"${tpl.name}" will be removed. Campaigns already created from it are unaffected.`,
            confirmText: "Delete template",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/campaign-templates/${tplId}`);
            toast.success("Template deleted");
            if (selectedTemplate === tplId) setSelectedTemplate("");
            loadTemplates();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        }
    };

    // When client changes, inherit their currency
    useEffect(() => {
        const c = clients.find((x) => x.id === form.client_org_id);
        if (c && c.currency) {
            setForm((f) => ({ ...f, currency: c.currency }));
        }
    }, [form.client_org_id, clients]);

    // Auto-calculate Daily Run Rate
    const days = useMemo(() => daysBetween(form.start_date, form.end_date), [form.start_date, form.end_date]);
    const dailyRunRate = useMemo(() => {
        const vol = Number(form.volume || 0);
        if (!days || !vol) return 0;
        return Math.round(vol / days);
    }, [form.volume, days]);

    const currencySymbol = getCurrencyMeta(form.currency)?.symbol || form.currency;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.client_org_id) {
            toast.error("Select a client.");
            return;
        }
        if (!form.start_date || !form.end_date) {
            toast.error("Set start and end dates.");
            return;
        }
        if (new Date(form.end_date) < new Date(form.start_date)) {
            toast.error("End date must be on or after start date.");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                volume: Number(form.volume) || 0,
                daily_run_rate: dailyRunRate,
                payout: Number(form.payout) || 0,
                revenue: Number(form.revenue) || 0,
                platform_type: form.platform_type,
                reference_file_id: referenceFile?.id || null,
            };
            const { data } = await api.post("/campaigns", payload);
            toast.success("Campaign created");
            navigate(`/campaigns/${data.id}`);
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-4xl">
            <header className="mb-8">
                <div className="overline mb-1">// New Intake</div>
                <h1 className="font-display text-4xl tracking-tighter">Create Campaign</h1>
                <p className="text-slate-500 mt-2 text-sm">Capture the brief now. Execution & delivery teams take it from here.</p>
            </header>

            <form onSubmit={onSubmit} className="bg-white border border-slate-200 p-6 md:p-8 space-y-8" data-testid="campaign-create-form">
                {templates.length > 0 && (
                    <div className="bg-slate-50 border border-slate-200 p-4 -mx-2 md:-mx-4 -mt-2" data-testid="template-picker">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                                <div className="overline">Start from</div>
                                <div className="text-sm">Pre-fill the form from a saved campaign template — you can still edit any field below.</div>
                            </div>
                            <div className="flex items-center gap-2 w-full md:w-auto">
                                <div className="flex-1 md:w-80">
                                    <Select value={selectedTemplate} onValueChange={applyTemplate}>
                                        <SelectTrigger data-testid="template-picker-select" className="rounded-none h-10 border-slate-300 bg-white">
                                            <SelectValue placeholder="Choose a template…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {templates.map((t) => (
                                                <SelectItem key={t.id} value={t.id}>
                                                    {t.name}
                                                    {t.client_org_name && (
                                                        <span className="text-slate-400 font-mono text-[10px] ml-2">{t.client_org_name}</span>
                                                    )}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {selectedTemplate && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setEditingTemplate(templates.find((t) => t.id === selectedTemplate))}
                                            data-testid="template-edit-button"
                                            title="Edit template"
                                            className="h-10 px-3 bg-white border border-slate-300 hover:border-slate-900 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => deleteTemplate(selectedTemplate)}
                                            data-testid="template-delete-button"
                                            title="Delete template"
                                            className="h-10 px-3 bg-white border border-slate-300 hover:border-red-700 hover:text-red-700 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5"
                                        >
                                            <Trash size={12} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <Section title="Client & Campaign">
                    <Field label="Client">
                        <Select value={form.client_org_id} onValueChange={(v) => setForm({ ...form, client_org_id: v })}>
                            <SelectTrigger data-testid="form-client" className="rounded-none h-10 border-slate-300">
                                <SelectValue placeholder="Select client" />
                            </SelectTrigger>
                            <SelectContent>
                                {clients.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.name}
                                        {c.currency && (
                                            <span className="text-slate-400 font-mono text-[10px] ml-2">{c.currency}</span>
                                        )}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Campaign name">
                        <Input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-slate-300" placeholder="e.g. Q1 Awareness — Conversion Lift" />
                    </Field>
                </Section>

                <Section title="Dates & Run Rate">
                    <Field label="Start date">
                        <Input
                            type="date"
                            data-testid="form-start-date"
                            value={form.start_date}
                            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                            className="rounded-none border-slate-300 font-mono h-10"
                            required
                        />
                    </Field>
                    <Field label="End date">
                        <Input
                            type="date"
                            data-testid="form-end-date"
                            value={form.end_date}
                            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                            min={form.start_date}
                            className="rounded-none border-slate-300 font-mono h-10"
                            required
                        />
                    </Field>
                    <Field label="Total volume">
                        <Input
                            data-testid="form-volume"
                            type="number"
                            value={form.volume}
                            onChange={(e) => setForm({ ...form, volume: e.target.value })}
                            className="rounded-none border-slate-300 font-mono"
                            placeholder="e.g. 12000000"
                        />
                    </Field>
                    <Field label={`Daily run rate · auto (${days || 0} days)`}>
                        <div
                            data-testid="form-drr-auto"
                            className="h-10 border border-slate-200 bg-slate-50 px-3 inline-flex items-center font-mono text-slate-700"
                        >
                            {dailyRunRate ? dailyRunRate.toLocaleString() : "—"}
                            <span className="font-mono text-[10px] text-slate-400 ml-2 uppercase tracking-wider">volume ÷ days</span>
                        </div>
                    </Field>
                </Section>

                <Section title="Commercials">
                    <Field label="Currency">
                        <CurrencyPicker
                            value={form.currency}
                            onChange={(v) => setForm({ ...form, currency: v })}
                            testid="form-currency"
                        />
                    </Field>
                    <Field label={`Payout (${currencySymbol})`}>
                        <Input data-testid="form-payout" type="number" value={form.payout} onChange={(e) => setForm({ ...form, payout: e.target.value })} className="rounded-none border-slate-300 font-mono" placeholder="0" />
                    </Field>
                    <Field label={`Revenue · estimated (${currencySymbol})`}>
                        <Input data-testid="form-revenue" type="number" value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} className="rounded-none border-slate-300 font-mono" placeholder="0" />
                    </Field>
                    <Field label="Pricing model">
                        <Select value={form.pricing_model} onValueChange={(v) => setForm({ ...form, pricing_model: v })}>
                            <SelectTrigger data-testid="form-pricing" className="rounded-none h-10 border-slate-300"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PRICING_MODELS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Platform">
                        <Select value={form.platform_type} onValueChange={(v) => setForm({ ...form, platform_type: v })}>
                            <SelectTrigger data-testid="form-platform-type" className="rounded-none h-10 border-slate-300"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PLATFORM_TYPES.map((p) => <SelectItem key={p.value} value={p.value} data-testid={`form-platform-type-option-${p.value}`}>{p.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                </Section>

                <Section title="Targeting & Creative">
                    <Field label="Creative type">
                        <Select value={form.creative_type} onValueChange={(v) => setForm({ ...form, creative_type: v })}>
                            <SelectTrigger data-testid="form-creative-type" className="rounded-none h-10 border-slate-300"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {CREATIVE_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Targeting" full>
                        <Textarea data-testid="form-targeting" value={form.targeting} onChange={(e) => setForm({ ...form, targeting: e.target.value })} className="rounded-none border-slate-300" placeholder="audience, geography, interests…" rows={2} />
                    </Field>
                </Section>

                <Section title="Reference brief / image (optional)">
                    <Field label="Attach brief, deck, or reference image" full>
                        <FileUploader
                            value={referenceFile}
                            onChange={setReferenceFile}
                            accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx"
                            maxSizeMb={50}
                            testid="form-reference-file"
                        />
                    </Field>
                </Section>

                <Section title="Notes">
                    <Field label="Notes" full>
                        <Textarea data-testid="form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none border-slate-300" rows={3} placeholder="Brief, internal notes, or follow-ups…" />
                    </Field>
                </Section>

                <div className="bg-slate-50 border border-slate-200 px-5 py-3 text-xs text-slate-600">
                    <span className="font-mono uppercase tracking-wider text-[10px] text-slate-500 mr-2">Next step:</span>
                    Creatives and platform allocation are added on the campaign detail page after intake is saved.
                </div>

                <div className="flex justify-end gap-3 border-t border-slate-200 pt-6">
                    <Button type="button" variant="outline" onClick={() => navigate("/campaigns")} className="rounded-none border-slate-300">Cancel</Button>
                    <Button type="submit" disabled={busy} data-testid="form-submit" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6">
                        {busy ? "Creating…" : "Create Campaign"}
                    </Button>
                </div>
            </form>

            {editingTemplate && (
                <EditTemplateModal
                    template={editingTemplate}
                    clients={clients}
                    onClose={() => setEditingTemplate(null)}
                    onSaved={() => {
                        setEditingTemplate(null);
                        loadTemplates();
                        toast.success("Template updated");
                    }}
                />
            )}
        </div>
    );
}


function Section({ title, children }) {
    return (
        <div>
            <div className="overline mb-4">{title}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
        </div>
    );
}

function Field({ label, children, full = false }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <Label className="overline mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}
